import { uniqBy } from 'lodash-es';
import type { AdSetEntry } from '../api/getAdSets';

export interface UseAdSetBudgetSumsParams {
  selectedAdSets: AdSetEntry[];
  budgets: Record<string, string | number>;
  campaignBudgets: Record<string, string | number>;
  duplicateCounts: Record<string, string | number>;
}

export interface UseAdSetBudgetSumsResult {
  cboSum: number;
  aboSum: number;
  totalSum: number;
}

export const useAdSetBudgetSums = (
  params: UseAdSetBudgetSumsParams,
): UseAdSetBudgetSumsResult => {
  const { selectedAdSets, budgets, campaignBudgets, duplicateCounts } = params;

  // Get unique CBO ad sets by campaign_id to avoid double counting
  const cboAdSets = uniqBy(
    selectedAdSets.filter((adSet: AdSetEntry) => adSet.is_cbo),
    'campaign_id',
  );

  // Get all ABO ad sets
  const aboAdSets = selectedAdSets.filter((adSet: AdSetEntry) => !adSet.is_cbo);

  const cboSum = cboAdSets.reduce((total, adSet) => {
    const budget = Number(campaignBudgets[adSet.campaign_id]);
    return isNaN(budget) ? total : total + budget;
  }, 0);

  const aboSum = aboAdSets.reduce((total, { id }) => {
    const budget = Number(budgets[id]);
    if (isNaN(budget)) return total;

    // Mirrors getDuplicatesCount in utils/calculateAdsCount.ts: an ad set
    // without a duplicate count yet (e.g. its grid cell hasn't mounted)
    // still counts once, instead of dropping its budget to 0.
    const rawDuplicates = Number(duplicateCounts[id]);
    const duplicates = Number.isFinite(rawDuplicates) && rawDuplicates >= 1
      ? rawDuplicates
      : 1;

    return total + budget * duplicates;
  }, 0);

  const totalSum = cboSum + aboSum;

  return { cboSum, aboSum, totalSum };
};

export default useAdSetBudgetSums;
